import React, { useState } from 'react';
import { BillData } from '../types/billing';
import { paymentService, createPaymentDataFromBill } from '../services/paymentService';
import { formatAmount } from '../utils/formatters';
import { useNotificationHelpers } from './NotificationSystem';
import LoadingSpinner from './LoadingSpinner';

import { PaymentRecord } from '../types/billing';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  bill: BillData | null;
  clientEmail?: string;
  onPaymentSuccess?: (paymentId: string, paymentRecord?: Omit<PaymentRecord, 'id'>) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  bill,
  clientEmail,
  onPaymentSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { showSuccess, showError } = useNotificationHelpers();

  if (!isOpen || !bill) return null;

  const outstandingAmount = bill.totalAmount; // In production, calculate based on payments made

  const handlePayment = async () => {
    if (!bill) return;

    setIsProcessing(true);
    try {
      const paymentData = createPaymentDataFromBill(bill, clientEmail);
      const result = await paymentService.initiatePayment(paymentData);

      if (result.success) {
        await handlePaymentSuccess(result.paymentId || '', 'Razorpay');
      } else {
        showError('Payment Failed', result.error || 'Payment was not completed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showError('Payment Error', 'An error occurred while processing your payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDummyPayment = async () => {
    if (!bill) return;

    setIsProcessing(true);
    try {
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      const dummyPaymentId = `demo_${Date.now()}`;
      await handlePaymentSuccess(dummyPaymentId, 'Demo');
    } catch (error) {
      console.error('Dummy payment error:', error);
      showError('Payment Error', 'An error occurred while processing your payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentId: string, method: string) => {
    if (!bill) return;

    try {
      // Record payment in database
      const paymentRecord: Omit<PaymentRecord, 'id'> = {
        companyId: bill.clientId,
        billId: bill.id,
        amount: outstandingAmount,
        date: new Date().toISOString(),
        mode: method === 'Razorpay' ? 'UPI' : 'Other',
        reference: paymentId,
        notes: `Payment received via ${method}`
      };

      // Call parent component to record payment
      await onPaymentSuccess?.(paymentId, paymentRecord);

      showSuccess('Payment Successful', `Your payment of ${formatAmount(outstandingAmount)} has been processed successfully via ${method}!`);
      onClose();
    } catch (error) {
      console.error('Error recording payment:', error);
      showError('Payment Error', 'Payment was processed but failed to record. Please contact support.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-avis-blue to-avis-dark text-white px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">Make Payment</h2>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
              disabled={isProcessing}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Bill Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Details</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice Number:</span>
                <span className="font-semibold">{bill.invoiceNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service:</span>
                <span className="font-semibold">{bill.items.length > 0 ? bill.items[0].description : 'NDT Services'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Due:</span>
                <span className="font-bold text-lg text-green-600">{formatAmount(outstandingAmount)}</span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-avis-blue rounded-full mt-2 flex-shrink-0"></div>
                <p>You will be redirected to a secure payment gateway</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-avis-blue rounded-full mt-2 flex-shrink-0"></div>
                <p>Supported payment methods: Credit/Debit Cards, UPI, Net Banking, Wallets</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-avis-blue rounded-full mt-2 flex-shrink-0"></div>
                <p>All transactions are secured with 256-bit SSL encryption</p>
              </div>
            </div>
          </div>

          {/* Payment Options */}
          <div className="space-y-4">
            {/* Real Payment Button */}
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full bg-avis-blue text-white py-3 px-4 rounded-lg hover:bg-avis-dark transition-colors font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span>Pay with Razorpay</span>
                </>
              )}
            </button>

            {/* Dummy Payment Button */}
            <button
              onClick={handleDummyPayment}
              disabled={isProcessing}
              className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Demo Payment (Test)</span>
            </button>

            <button
              onClick={onClose}
              disabled={isProcessing}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>

          {/* Footer Note */}
          <div className="mt-4 text-xs text-gray-500 text-center">
            Powered by Razorpay • Secure Payment Gateway
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
